'use client';

import clsx from "clsx";
import { useLenis } from "lenis/react";
import Image from 'next/image';
import { useState, useRef, useEffect, useCallback } from 'react';
import CtaBanner from "@/components/cta-banner/cta-banner";
import AudioWave from "@/components/icon/audio-wave";
import Pause from "@/components/icon/pause";
import Play from "@/components/icon/play";
import Transcript from "@/components/icon/transcript";
import ZoomIn from "@/components/icon/zoom-in";
import RecordTranscript from "@/components/records/record-transcript/record-transcript";
import RecordViewer from "@/components/records/record-viewer/record-viewer";
import RecordsImageCollage from "@/components/records/records-image-collage/records-image-collage";
import StoryOverlay from "@/components/story-overlay/story-overlay";
import { STORIES_BASE_PATH } from "@/constants/routes";
import { useApp } from "@/context/AppContext";
import useImageLoad from "@/hooks/useImageLoad";
import useSwipeNavigation from "@/hooks/useSwipeNavigation";


export default function RecordStoryLayout({
  story,
  category,
  theme,
  stories,
  className = "",
}) {
  const {
    previousStoryListingRoute,
    isRouteChanging,
    previousStoryColor,
    setPreviousStoryColor,
    currentTransitionName,
    setRecordCategoryType,
  } = useApp();
  const isSlideTransition =
    currentTransitionName === "slide-left" ||
    currentTransitionName === "slide-right";
  const bgRef = useRef(null);

  const [isRecordViewerOpen, setIsRecordViewerOpen] = useState(false);
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(null); // Seconds remaining
  const [isUiMounted, setIsArrowNavMounted] = useState(false);
  const [recordPreviewBounds, setRecordPreviewBounds] = useState(null);
  const audioRef = useRef(null);
  const scrubContainerRef = useRef(null);
  const isScrubbing = useRef(false);
  const closeButtonRef = useRef(null);
  const prevButtonRef = useRef(null);
  const nextButtonRef = useRef(null);
  const wasPlayingBeforeBlurRef = useRef(false);
  const recordPreviewImageRef = useRef(null);
  const lenis = useLenis();

  /**
   * Gets the previous and next story routes with wrap-around navigation.
   * If current story is first, previous wraps to last. If current is last, next wraps to first.
   * @param {Array} stories - Array of story objects to navigate through
   * @param {Function} buildRoute - Function that takes a story and returns its route URL
   * @returns {{ previousRoute: string, nextRoute: string }}
   */
  const getAdjacentStories = (buildRoute) => {
    const currentIndex = stories.findIndex((s) => s.slug === story.slug);
    const previousIndex =
      currentIndex === 0 ? stories.length - 1 : currentIndex - 1;
    const nextIndex =
      currentIndex === stories.length - 1 ? 0 : currentIndex + 1;

    return {
      previousRoute: buildRoute(stories[previousIndex]),
      nextRoute: buildRoute(stories[nextIndex]),
    };
  };

  // Determine story navigation based on where user came from:
  // - From overview page (${STORIES_BASE_PATH}/record-gallery/ or /stories): navigate through all stories
  // - From category page: navigate only within that category
  // const navigatedFromOverviewPage =
  //   storiesOverviewRoutes.includes(previousStoryListingRoute) || !previousStoryListingRoute;

  // const stories = navigatedFromOverviewPage
  //   ? storiesData
  //   : storiesData.filter(story => story.category === category.slug);

  const buildRoute = (story) =>
    `${STORIES_BASE_PATH}/record-gallery/${story.category}/${story.slug}`;

  const { previousRoute: previousStoryRoute, nextRoute: nextStoryRoute } =
    getAdjacentStories(buildRoute);

  /**
   * Formats seconds into mm:ss display format
   * @param {number} seconds - Time in seconds
   * @returns {string} Formatted time string (e.g., "1:35")
   */
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    if (isRecordViewerOpen || isTranscriptOpen) {
      lenis?.stop();
    } else {
      lenis?.start();
    }

    return () => lenis?.start();
  }, [isRecordViewerOpen, isTranscriptOpen, lenis]);

  // Set up audio event listeners for progress tracking
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setTimeRemaining(audio.duration);
    };

    const handleTimeUpdate = () => {
      if (audio.duration) {
        setTimeRemaining(audio.duration - audio.currentTime);
        setAudioProgress(audio.currentTime / audio.duration);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setTimeRemaining(audio.duration);
      setAudioProgress(0);
    };

    // Check if audio.duration already has a value (metadata is already available)
    // If so, set timeRemaining immediately without waiting for an event that already fired
    if (audio.duration) {
      setTimeRemaining(audio.duration);
    }

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
    };
  }, []);

  // Pause audio on window blur, resume on focus if it was playing
  useEffect(() => {
    const handleBlur = () => {
      if (isPlaying) {
        wasPlayingBeforeBlurRef.current = true;
        audioRef.current?.pause();
        setIsPlaying(false);
      }
    };

    const handleFocus = () => {
      if (wasPlayingBeforeBlurRef.current) {
        wasPlayingBeforeBlurRef.current = false;
        audioRef.current?.play();
        setIsPlaying(true);
      }
    };

    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
    };
  }, [isPlaying]);

  // Fade in arrow navigation after mount
  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsArrowNavMounted(true);
    }, 100);

    return () => clearTimeout(timeout);
  }, []);

  // Keyboard navigation: ESC to close, left/right arrows to navigate stories
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isRecordViewerOpen || isTranscriptOpen) return;

      switch (e.key) {
      case "Escape":
        closeButtonRef.current?.click();
        break;
      case "ArrowLeft":
        prevButtonRef.current?.click();
        break;
      case "ArrowRight":
        nextButtonRef.current?.click();
        break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isRecordViewerOpen, isTranscriptOpen]);

  // Touch swipe navigation: swipe left/right to navigate stories
  useSwipeNavigation({
    onSwipeLeft: () => nextButtonRef.current?.click(),
    onSwipeRight: () => prevButtonRef.current?.click(),
    disabled: isRecordViewerOpen || isTranscriptOpen,
  });

  // Animate background color from previous theme to current theme after slide transition finishes
  useEffect(() => {
    if (!bgRef.current) return;

    // When route change finishes and we have a previous theme, animate to current theme
    if (!isRouteChanging && previousStoryColor) {
      bgRef.current.style.backgroundColor = "";
      setPreviousStoryColor(null);
    }
  }, [isRouteChanging, previousStoryColor, setPreviousStoryColor]);

  /** Opens or closes the record viewer */
  const toggleRecordViewer = (isOpen) => {
    if (isOpen && recordPreviewImageRef.current) {
      setRecordPreviewBounds(
        recordPreviewImageRef.current.getBoundingClientRect(),
      );
    }
    setIsRecordViewerOpen(isOpen);
  };

  /** Opens or closes the audio transcript */
  const toggleTranscript = (isOpen) => {
    setIsTranscriptOpen(isOpen);
  };

  /** Toggles audio playback between play and pause */
  const toggleAudio = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }

      setIsPlaying(!isPlaying);
    }
  };

  /** Store current theme color before navigating to another story */
  const handleStoryNavigation = () => {
    // Get the computed color value from the CSS variable
    const computedColor = getComputedStyle(bgRef.current).backgroundColor;
    setPreviousStoryColor(computedColor);
  };

  /** Seek audio to the position corresponding to the pointer's X within the scrub container */
  const seekToPointer = useCallback((e) => {
    const container = scrubContainerRef.current;
    const audio = audioRef.current;
    if (!container || !audio || !audio.duration) return;

    const rect = container.getBoundingClientRect();
    const ratio = Math.max(
      0,
      Math.min(1, (e.clientX - rect.left) / rect.width),
    );
    audio.currentTime = ratio * audio.duration;
    setAudioProgress(ratio);
    setTimeRemaining(audio.duration - audio.currentTime);
  }, []);

  /** Handle pointer down on the scrub area — start scrubbing */
  const handleScrubPointerDown = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      isScrubbing.current = true;
      seekToPointer(e);

      const handlePointerMove = (e) => {
        if (isScrubbing.current) seekToPointer(e);
      };

      const handlePointerUp = () => {
        isScrubbing.current = false;
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
      };

      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
    },
    [seekToPointer],
  );

  const thumbnailImageLoad = useImageLoad();

  return (
    <>
      <StoryOverlay
        theme={theme}
        previousRoute={previousStoryRoute}
        nextRoute={nextStoryRoute}
        closeHref={
          previousStoryListingRoute || `${STORIES_BASE_PATH}/record-gallery`
        }
        onNavigate={handleStoryNavigation}
        isVisible={isUiMounted && !(isRouteChanging && isSlideTransition)}
        showCloseButton={!isTranscriptOpen}
        previousColor={previousStoryColor}
        bgRef={bgRef}
        prevRef={prevButtonRef}
        nextRef={nextButtonRef}
        closeRef={closeButtonRef}
        className={className}
        inert={isRecordViewerOpen || isTranscriptOpen}
      >
        <main className={clsx("grid-9 items-center mt-21.5 lg:mt-0")}>
          <RecordsImageCollage attribution={story.attribution} image={story.content.image} />

          <article className="col-span-full mt-7 lg:[grid-column:6/-1] lg:mt-0">
            <p className="heading-7-small text-main-dark xl:heading-8 opacity-60">
              {category.title}
            </p>
            <h1 className="heading-2-small xl:heading-2 mt-4 text-pretty text-[#212121]" dangerouslySetInnerHTML={{ __html: story.content.title }}>
            </h1>
            <div className="body-1-sans-small xl:body-2-sans-bold mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 lg:gap-x-3.5">
              <span className="shrink-0">{story.content.name}</span>
              <span
                className="h-[5px] w-[5px] shrink-0 rounded-full bg-current"
                aria-hidden="true"
              />
              <span className="shrink-0">{story.content.location}</span>
              <span
                className="h-[5px] w-[5px] shrink-0 rounded-full bg-current"
                aria-hidden="true"
              />
              <span className="shrink-0">{story.content.date}</span>
            </div>
            <p
              className="body-1-small xl:body-1 mt-9 text-pretty text-[#141414]"
              dangerouslySetInnerHTML={{ __html: story.content.text }}
            ></p>

            <div className="border-main-dark/[0.3] mt-9 flex flex-col gap-y-3.25 border-b pb-6 lg:gap-y-4 lg:pb-9">
              {story.content.record && (
                <button
                  className="group relative flex h-20 cursor-pointer items-center justify-between gap-x-1 rounded-lg px-4.5 before:pointer-events-none before:absolute before:inset-0 before:[transform:translateZ(0)] before:rounded-lg before:bg-black/35 before:mix-blend-overlay before:transition-colors hover:before:bg-black/55 lg:h-24 lg:gap-x-(--space-6) lg:px-6"
                  type="button"
                  aria-label={`View record: ${story.content.record.title}`}
                  onClick={() => toggleRecordViewer(true)}
                >
                  <div className="flex cursor-pointer items-center gap-x-3 lg:gap-x-4.5">
                    <ZoomIn className="shrink-0" aria-hidden="true" />
                    <span className="body-2-sans-bold-small xl:body-1-sans text-left">
                      View record
                    </span>
                  </div>
                  <div className="relative w-41.5 h-full [clip-path:inset(-100vh_-100vw_0_-100vw)] lg:w-62.5" aria-hidden="true">
                    <div ref={recordPreviewImageRef} className="absolute inset-0 top-[1.3125rem]  motion-safe:[transition:transform_250ms_var(--custom-ease-out)] [will-change:transform] group-hover:[transform:translateY(-2.1875rem)_scale(1.08)]">
                      <Image unoptimized={true} {...story.content.record.thumbnail} ref={thumbnailImageLoad.imageRef} onLoad={thumbnailImageLoad.onLoad} className="opacity-0"/>
                    </div>
                  </div>
                </button>
              )}

              {story.content.audioTrack.src && (
                <div
                  className="relative flex h-20 cursor-pointer items-center justify-between gap-x-1 overflow-hidden rounded-lg px-4.5 before:pointer-events-none before:absolute before:inset-0 before:[transform:translateZ(0)] before:bg-black/35 before:mix-blend-overlay before:transition-colors hover:before:bg-black/55 lg:h-24 lg:gap-x-(--space-6) lg:px-6"
                  role="button"
                  tabIndex={0}
                  aria-label={isPlaying ? "Pause story" : "Play story"}
                  aria-pressed={isPlaying}
                  onClick={toggleAudio}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleAudio();
                    }
                  }}
                >
                  <div className="flex items-center gap-x-3 lg:gap-x-4.5">
                    {isPlaying ? (
                      <Pause className="shrink-0" aria-hidden="true" />
                    ) : (
                      <Play className="shrink-0" aria-hidden="true" />
                    )}
                    <span className="body-2-sans-bold-small xl:body-1-sans text-left">
                      Listen to story
                    </span>
                  </div>

                  <div className="flex w-41.5 items-center gap-x-2 lg:w-62.5 lg:gap-x-2.5">
                    <div
                      ref={scrubContainerRef}
                      className="cursor-pointer touch-none"
                      onPointerDown={handleScrubPointerDown}
                      onClick={(e) => e.stopPropagation()}
                      role="slider"
                      aria-label="Seek audio"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={Math.round(audioProgress * 100)}
                      tabIndex={0}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <AudioWave isPlaying={isPlaying} progress={audioProgress} aria-hidden="true" />
                    </div>

                    <div
                      className="body-2-sans-bold-small xl:body-1-sans tabular-nums"
                      aria-live="polite"
                      aria-atomic="true"
                      aria-label={
                        timeRemaining !== null
                          ? `${formatTime(timeRemaining)} remaining`
                          : "Loading audio"
                      }
                    >
                      {timeRemaining !== null
                        ? formatTime(timeRemaining)
                        : "--:--"}
                    </div>
                    <button
                      aria-label="View transcript"
                      aria-expanded={isTranscriptOpen}
                      className="min-h-6 min-w-6 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleTranscript(true);
                      }}
                      onKeyDown={(e) => e.stopPropagation()}
                      type="button"
                    >
                      <Transcript aria-hidden="true" />
                    </button>
                  </div>

                  <audio ref={audioRef} aria-hidden="true">
                    <source
                      src={story.content.audioTrack.src}
                      type="audio/mpeg"
                    />
                  </audio>
                </div>
              )}
            </div>

            <CtaBanner text="Learn about your family story in records, too."/>
          </article>
        </main>
      </StoryOverlay>

      {isRecordViewerOpen && story.content.record && (
        <RecordViewer
          previewBounds={recordPreviewBounds}
          onClose={() => toggleRecordViewer(false)}
          {...story.content.record}
        />
      )}

      {isTranscriptOpen && story.content.audioTrack?.transcript && (
        <RecordTranscript
          paragraphs={story.content.audioTrack.transcript}
          onClose={() => toggleTranscript(false)}
        />
      )}
    </>
  );
}
